import {
  ButtonGroup,
  IconButton,
  Stack,
  Text,
  useDisclosure,
  Button,
} from "@chakra-ui/react";
import { observer } from "mobx-react";
import { t } from "i18next";
import { CraWayItem } from "./CraWayItem";
import { SurveySharingModal } from "../../../../../shared/views/SharingModal/SurveySharingModal";
import { useNavigate } from "react-router-dom";
import { appRoutes } from "../../../../../../App";
import { CreateOrUpdateSurveyModal } from "../../../../../survey/views/CreateOrUpdateSurvey/CreateOrUpdateSurveyModal";
import { CreateOrUpdateCraWayModal } from "../../../../../cra/views/CreateOrUpdateRoute/CreateOrUpdateCraWayModal";
import { useStores } from "../../../../../shared/stores/RootStore";
import { toast } from "../../../../../shared/views/CustomToaster";
import {
  AddIcon,
  DeleteIcon,
  EditIcon,
  UnlockIcon,
} from "../../../../../shared/views/icons";
import { CustomMenu } from "../../../../../shared/views/CustomMenu";
import { HlaStats } from "./HlaStats";

const i18prefix = "map:surveySummary";

const SurveyDetailBase: React.FC = () => {
  const { surveyStore, authStore, filterStore, mapQueryStore } = useStores();
  const surveySharingModal = useDisclosure();
  const surveyEditingModal = useDisclosure();
  const wayCreationModal = useDisclosure();

  const navigate = useNavigate();

  const survey = surveyStore.getSelected();

  const deleteSurvey = () => {
    if (window.confirm(t("shared:survey.delete.popUp"))) {
      surveyStore
        .deleteSurvey(survey!.id)
        .then(() => {
          toast("success", t("shared:survey.delete.success"));
        })
        .catch((e) => {
          toast("error", e.response?.statusText);
        })
        .finally(() => {
          navigate(appRoutes.home());
        });
    }
  };

  if (!survey) return null;

  return (
    <Stack py={3} overflowY={"auto"} gap={4}>
      <Stack>
        <Text
          minH={7}
          flexGrow={1}
          px={2}
          fontWeight="semibold"
          textAlign="center"
        >
          {survey.name}
        </Text>
        {authStore.isAuthenticated && (
          <ButtonGroup
            alignSelf="center"
            variant="outline"
            size="sm"
            attached
            colorPalette="primary"
          >
            <IconButton
              title={t(`${i18prefix}.edit`)}
              disabled={!survey.permissions.canUpdate}
              onClick={() => {
                surveyEditingModal.onOpen();
              }}
            >
              <EditIcon />
            </IconButton>

            <IconButton
              title={t(`${i18prefix}.share`)}
              disabled={!survey.permissions.canShare}
              onClick={() => {
                surveySharingModal.onOpen();
              }}
            >
              <UnlockIcon />
            </IconButton>

            <IconButton
              title={t(`${i18prefix}.delete`)}
              disabled={!survey.permissions.canDelete}
              onClick={deleteSurvey}
            >
              <DeleteIcon />
            </IconButton>
          </ButtonGroup>
        )}
        {authStore.isAuthenticated && (
          <CustomMenu
            items={[
              {
                key: "cyclerap",
                text: "CycleRAP assessment",
                description:
                  "Routes are carefully analyzed according to the CycleRAP guidelines.",
                onClick: () => {
                  wayCreationModal.onOpen();
                },
              },
              {
                key: "high-level",
                text: "High-level assessment",
                description:
                  "A more scalable and less precise assessment of the infrastructure.",
                onClick: () => {
                  mapQueryStore.activate();
                  filterStore.resetHlaFilters();
                },
              },
            ]}
          >
            <Button
              alignSelf={"center"}
              size="sm"
              fontSize="sm"
              colorPalette="primary"
              variant="ghost"
              disabled={!survey.permissions.canCreate}
            >
              <AddIcon /> Start assessment
            </Button>
          </CustomMenu>
        )}
      </Stack>

      <HlaStats />

      <Stack px={4}>
        <Text
          fontSize="xs"
          textTransform={"uppercase"}
          fontWeight={"semibold"}
          color={"gray.700"}
          pt={2}
        >
          CycleRAP assessments
        </Text>
        {!!filterStore.filteredCraWays.length ? (
          <Stack gap={2}>
            {filterStore.filteredCraWays.map((craWay) => (
              <CraWayItem key={craWay.id} craWay={craWay} />
            ))}
          </Stack>
        ) : (
          <Text fontSize="sm">{t(`${i18prefix}.noRoutes`)}</Text>
        )}
      </Stack>

      {surveySharingModal.open && (
        <SurveySharingModal onClose={surveySharingModal.onClose} />
      )}

      {surveyEditingModal.open && (
        <CreateOrUpdateSurveyModal
          survey={survey}
          onClose={surveyEditingModal.onClose}
        />
      )}

      {wayCreationModal.open && (
        <CreateOrUpdateCraWayModal onClose={wayCreationModal.onClose} />
      )}
    </Stack>
  );
};

export const SurveyDetail = observer(SurveyDetailBase);
